import React from 'react';
import mammoth from 'mammoth';
import styled from 'styled-components';
import { Body1, Body5, Container } from '@ui';
import { FormState } from '@ui/FormState';
import { Working } from '@ui/Working';
import { handle400Statuses, sendApiDownloadRequest } from '@/services/api';
import { logError, logNetRequest } from '@/utils';
import { RfpTemplateResultsContext } from './RfpTemplateResultsContext';
import { parsePptxTemplate } from './pptxPreview/parsePptxTemplate';
import {
  getNestedAnswerEntries,
  replaceTemplateKeysWithAnswers,
  TEMPLATE_PREVIEW_ANSWER_CLASS,
  TEMPLATE_PREVIEW_PLACEHOLDER_CLASS,
} from './rfpTemplateAnswerUtils';
import { ResultCard } from './styles';
import { getTemplateBinaryConfig } from './templateActions/templateBinaryUtils';

const AnswerText = styled.div`
  white-space: pre-wrap;
`;

const NestedAnswerRow = styled.div`
  & + & {
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder};
  }
`;

const PreviewCard = styled(ResultCard)`
  overflow-x: auto;
`;

const TemplatePreview = styled.div`
  color: ${({ theme }) => theme.themeColors.text};
  line-height: 1.2;
  font-size: 14px;
  font-weight: 800;

  .${TEMPLATE_PREVIEW_ANSWER_CLASS} {
    font-weight: 300;
  }

  .${TEMPLATE_PREVIEW_PLACEHOLDER_CLASS} {
    color: ${({ theme }) => theme.themeColors.componentNoOpacity};
    user-select: none;
  }

  > *:first-child {
    margin-top: 0;
  }

  > *:last-child {
    margin-bottom: 0;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    color: ${({ theme }) => theme.themeColors.text};
    font-weight: 700;
    line-height: 1.3;
    margin: 24px 0 12px;
  }

  h1 {
    font-size: 28px;
  }

  h2 {
    font-size: 22px;
  }

  h3 {
    font-size: 18px;
  }

  p {
    margin: 0 0 12px;
    line-height: 1.2;
  }

  ul,
  ol {
    margin: 0 0 16px;
    padding-left: 24px;
  }

  li {
    margin-bottom: 12px;
  }

  strong {
    font-weight: 700;
  }

  em {
    font-style: italic;
  }

  a {
    color: ${({ theme }) => theme.themeColors.text};
    text-decoration: underline;
  }

  blockquote {
    margin: 16px 0;
    padding-left: 16px;
    border-left: 3px solid ${({ theme }) => theme.themeColors.lowContrastBorder};
  }

  pre {
    background-color: ${({ theme }) => theme.themeColors.lowContrastBorder};
    border-left: 3px solid ${({ theme }) => theme.themeColors.primaryCtaButton};
    border-radius: 2px;
    margin: 16px 0;
    padding: 16px 20px;
    overflow-x: auto;
  }

  code {
    padding: 0 3px;
  }

  table {
    width: 100%;
    margin: 16px 0;
    border-collapse: collapse;
    table-layout: auto;
  }

  td,
  th {
    padding: 8px;
    border: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder};
    vertical-align: top;
  }

  tr > *:first-child {
    min-width: 72px;
  }

  th {
    font-weight: 700;
    background: ${({ theme }) => theme.themeColors.appBackground};
  }

  img {
    display: block;
    max-width: 100%;
    height: auto;
    margin: 16px auto;
  }

  .docx-title {
    margin-top: 0;
  }

  .docx-subtitle {
    color: ${({ theme }) => theme.themeColors.secondaryText};
    font-size: 18px;
    font-weight: 600;
  }

  .pptx-preview-document {
    display: flex;
    flex-direction: column;
    gap: 24px;
  }

  .pptx-preview-slide {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .pptx-preview-slide-header {
    color: ${({ theme }) => theme.themeColors.secondaryText};
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  .pptx-preview-slide-canvas {
    position: relative;
    max-width: 100%;
    border: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder};
    border-radius: 8px;
    background: ${({ theme }) => theme.themeColors.appBackground};
    overflow: hidden;
    transform-origin: top left;
  }

  .pptx-preview-shape {
    position: absolute;
    overflow: hidden;
  }

  .pptx-preview-text-frame,
  .pptx-preview-table-frame {
    width: 100%;
    height: 100%;
  }

  .pptx-preview-slide img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    margin: 0;
  }

  .pptx-preview-slide table {
    margin: 0 0 16px;
    height: 100%;
  }

  .pptx-preview-slide td,
  .pptx-preview-slide th {
    font-weight: 400;
    background: transparent;
  }
`;

const TEMPLATE_STYLE_MAP = [
  "p[style-name='Title'] => h1.docx-title:fresh",
  "p[style-name='Subtitle'] => h2.docx-subtitle:fresh",
];

const MAMMOTH_OPTIONS = {
  styleMap: TEMPLATE_STYLE_MAP,
  convertImage: mammoth.images.imgElement((image) =>
    image.read('base64').then((imageBuffer) => ({
      src: `data:${image.contentType};base64,${imageBuffer}`,
    }))
  ),
};

function AnswerEntry({ id, templateKey, answer }) {
  const isObjectAnswer = answer && typeof answer === 'object' && !Array.isArray(answer);
  const nestedAnswerEntries = isObjectAnswer ? getNestedAnswerEntries(answer) : [];
  const answerText = typeof answer === 'string' ? answer : '';

  return (
    <ResultCard key={id}>
      <Container bottom={8}>
        <Body5 bold>{templateKey}</Body5>
      </Container>
      <AnswerText>
        {nestedAnswerEntries.length > 0 ? (
          <>
            <Container bottom={12}>
              <Body5 isLowContrast>{`${nestedAnswerEntries.length} template values generated`}</Body5>
            </Container>
            {nestedAnswerEntries.map(({ templateKey: nestedTemplateKey, answer: nestedAnswer }) => (
              <NestedAnswerRow key={nestedTemplateKey}>
                <Container bottom={4}>
                  <Body5 bold>{nestedTemplateKey}</Body5>
                </Container>
                <Body5>{nestedAnswer}</Body5>
              </NestedAnswerRow>
            ))}
          </>
        ) : (
          <Body5 isLowContrast={isObjectAnswer && !answerText}>{answerText || 'Generating answer...'}</Body5>
        )}
      </AnswerText>
    </ResultCard>
  );
}

const SHOW_ANSWER_ENTRIES = false;

export function RfpTemplateAnswers({ templateId, template }) {
  const { answerEntries, hasStreamingPreview, streamError, streamStatus } = React.useContext(RfpTemplateResultsContext);
  const [templateHtml, setTemplateHtml] = React.useState('');
  const [isTemplatePreviewLoading, setIsTemplatePreviewLoading] = React.useState(false);
  const [templatePreviewError, setTemplatePreviewError] = React.useState(null);
  const templateBinaryConfig = React.useMemo(() => (template ? getTemplateBinaryConfig(template) : null), [template]);
  const isPowerPointPreview = Boolean(templateBinaryConfig?.isPowerPoint);
  const previewTitle = isPowerPointPreview ? 'Presentation Preview' : 'Document Preview';

  const renderedTemplateHtml = React.useMemo(
    () => replaceTemplateKeysWithAnswers(templateHtml, answerEntries),
    [answerEntries, templateHtml]
  );
  const shouldShowAnswerEntries = SHOW_ANSWER_ENTRIES || (!renderedTemplateHtml && !isTemplatePreviewLoading);

  React.useEffect(() => {
    if (!templateId || !hasStreamingPreview) {
      setTemplateHtml('');
      setTemplatePreviewError(null);
      setIsTemplatePreviewLoading(false);
      return () => {};
    }

    const cancelState = { isCanceled: false };

    const getTemplateDoc = async () => {
      setIsTemplatePreviewLoading(true);
      setTemplatePreviewError(null);

      try {
        const templatePathSuffix = isPowerPointPreview
          ? templateBinaryConfig?.endpointSuffix || 'template_pptx'
          : 'template_doc';
        const URL = `v1/genai/templates/${templateId}/${templatePathSuffix}`;
        const response = await sendApiDownloadRequest(URL);
        const status = response?.status;

        logNetRequest(status, null, URL);
        handle400Statuses(status);

        if (status === 200) {
          const blob = await response.blob();
          const arrayBuffer = await blob.arrayBuffer();
          const { messages, value } = isPowerPointPreview
            ? await parsePptxTemplate(arrayBuffer)
            : await mammoth.convertToHtml({ arrayBuffer }, MAMMOTH_OPTIONS);

          if (messages?.length > 0) {
            console.log('[RFP] template preview messages', {
              templateId,
              messages,
            });
          }

          if (!cancelState.isCanceled) {
            setTemplateHtml(value || '');
          }
          return;
        }

        if (!cancelState.isCanceled) {
          setTemplateHtml('');
          setTemplatePreviewError('Template preview unavailable. Answers are listed below.');
        }
      } catch (error) {
        logError(error, {
          component: 'RfpTemplateAnswers',
          message: 'Unable to load the RFP template preview.',
        });

        if (!cancelState.isCanceled) {
          setTemplateHtml('');
          setTemplatePreviewError('Template preview unavailable. Answers are listed below.');
        }
      } finally {
        if (!cancelState.isCanceled) {
          setIsTemplatePreviewLoading(false);
        }
      }
    };

    getTemplateDoc();

    return () => {
      cancelState.isCanceled = true;
    };
  }, [hasStreamingPreview, isPowerPointPreview, templateBinaryConfig, templateId]);

  if (answerEntries.length === 0 && streamStatus === 'pending' && hasStreamingPreview) {
    return (
      <Container top={24}>
        <Working />
      </Container>
    );
  }

  if (answerEntries.length === 0) {
    return null;
  }

  return (
    <Container top={24} id="rfp-template-answers">
      <FormState error={streamError} />
      {hasStreamingPreview && isTemplatePreviewLoading && (
        <Container top={streamError ? 16 : 0}>
          <Working message="Loading template preview" />
        </Container>
      )}
      {hasStreamingPreview && !isTemplatePreviewLoading && renderedTemplateHtml && (
        <Container top={streamError ? 16 : 0}>
          <PreviewCard>
            <Container bottom={24} top={8}>
              <Body1 bold>{previewTitle}</Body1>
            </Container>
            <TemplatePreview dangerouslySetInnerHTML={{ __html: renderedTemplateHtml }} />
          </PreviewCard>
        </Container>
      )}
      {hasStreamingPreview && !isTemplatePreviewLoading && templatePreviewError && (
        <Container top={streamError ? 16 : 0}>
          <Body5 isLowContrast>{templatePreviewError}</Body5>
        </Container>
      )}
      {shouldShowAnswerEntries && (
        <Container top={streamError || renderedTemplateHtml || templatePreviewError ? 16 : 0}>
          {answerEntries.map(({ id, templateKey, answer }) => (
            <AnswerEntry key={id} id={id} templateKey={templateKey} answer={answer} />
          ))}
        </Container>
      )}
    </Container>
  );
}
